import { getLanguageCode } from "../utils/language";
import { useTranslation } from "react-i18next";
import { AdFormat } from "../api/contracts/site-config";
import { useAdFormat, useHasAdFormatChoice } from "../hooks/useAdFormat";
import { useSelectAdFormat } from "../hooks/useSelectAdFormat";
import { Button } from "./Button";
import { resolveLucideIcon } from "../utils/getLucideIcon";

/** Fallback when the operator never named a format. */
const FORMAT_LABEL_KEY: Record<AdFormat, string> = {
  accommodation: "ad_format.accommodation",
  activity: "ad_format.activity",
  venue: "ad_format.venue",
  restaurant: "ad_format.restaurant",
  package: "ad_format.package",
  offer: "ad_format.offer",
};

type AdFormatTabsProps = {
  /** Controlled selection. Omit to follow the committed format. */
  value?: AdFormat;
  /**
   * Called instead of committing. The mobile sheet passes its draft setter so
   * a tap stages the format alongside dates and guests rather than swapping
   * the page out from under the half-filled form.
   */
  onSelect?: (module: AdFormat) => void;
};

/**
 * The format switcher above the search bar. Renders nothing unless the site
 * has enabled more than one format: a single button is not a choice, and an
 * empty strip reads as a broken feature rather than an inapplicable one.
 */
export const AdFormatTabs = ({ value, onSelect }: AdFormatTabsProps = {}) => {
  const { t } = useTranslation();
  const { format, options } = useAdFormat();
  const hasChoice = useHasAdFormatChoice();
  const selectAndRoute = useSelectAdFormat();

  if (!hasChoice) return null;

  const selectedFormat = value ?? format;
  const select = onSelect ?? selectAndRoute;

  const selectedLanguage = getLanguageCode();

  return (
    <div
      className="mx-auto -my-2 flex w-fit max-w-full items-center gap-2 overflow-x-auto py-2 [scrollbar-width:none] sm:flex-wrap sm:justify-center sm:overflow-visible [&>*]:shrink-0"
      role="tablist"
      aria-label={t("ad_format.aria_label")}
      data-testid="ad-format-tabs"
    >
      {options.map((option) => {
        // The operator's own label in the language being browsed, else our
        // own translation of the format. No hopping to another language the
        // operator happened to fill: we have a correct word for every format
        // in every language we ship, which beats showing English to a Swede.
        const named = option.names?.[selectedLanguage]?.trim();
        const label = named || t(FORMAT_LABEL_KEY[option.module]);
        const selected = option.module === selectedFormat;
        // No icon means no icon: an operator who picked none gets a bare label
        // rather than a stand-in they never chose.
        const Icon = option.icon ? resolveLucideIcon(option.icon) : null;

        return (
          <Button
            key={option.module}
            variant={selected ? "default" : "secondary"}
            size="chip"
            role="tab"
            aria-selected={selected}
            onClick={() => select(option.module)}
            data-testid={`ad-format-tab-${option.module}`}
          >
            {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden />}
            {label}
          </Button>
        );
      })}
    </div>
  );
};
